/**
 * Centralized Logger Utility
 *
 * Provides environment-aware logging with module prefixes and log levels.
 * In production, only warnings and errors are emitted.
 * In development, all levels are active.
 *
 * Usage:
 *   import { createLogger } from '@/lib/logger'
 *   const log = createLogger('MyModule')
 *   log.info('Loaded', { count: 42 })   // [MyModule] Loaded { count: 42 }
 *   log.warn('Slow query', ms)           // [MyModule] Slow query 340
 *   log.error('Failed', err)             // [MyModule] Failed Error: ...
 *   log.debug('Verbose detail')          // suppressed in production
 *
 * @module logger
 */

/** Log severity levels, ordered from most verbose to least. */
export const LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  SILENT: 4,
} as const

export type LogLevelName = keyof typeof LogLevel

/** Determine the active log level from the environment. */
function resolveLogLevel(): number {
  // Allow explicit override via VITE_LOG_LEVEL env var
  const explicit = (import.meta.env?.VITE_LOG_LEVEL as string)?.toUpperCase()
  if (explicit && explicit in LogLevel) {
    return LogLevel[explicit as LogLevelName]
  }

  // Default: production = WARN, everything else = DEBUG
  const isProd = import.meta.env?.PROD === true
    || import.meta.env?.MODE === 'production'
  return isProd ? LogLevel.WARN : LogLevel.DEBUG
}

const activeLevel = resolveLogLevel()

/**
 * Logger instance returned by `createLogger()`.
 * Each method accepts any number of arguments, matching `console.*` signatures.
 */
export interface Logger {
  /** Verbose debugging — suppressed in production. */
  debug: (...args: unknown[]) => void
  /** General informational messages — suppressed in production. */
  info: (...args: unknown[]) => void
  /** Potential issues that deserve attention. */
  warn: (...args: unknown[]) => void
  /** Errors that need investigation. */
  error: (...args: unknown[]) => void
}

/**
 * Create a prefixed logger bound to a module name.
 *
 * @param module - Short identifier for the calling module (e.g. 'Auth0Store', 'Router')
 * @returns Logger instance with debug/info/warn/error methods
 *
 * @example
 * ```ts
 * const log = createLogger('ShippingApi')
 * log.info('Fetching rates', { from: 'MI', to: 'CA' })
 * log.error('Rate fetch failed', error)
 * ```
 */
export function createLogger(module: string): Logger {
  const prefix = `[${module}]`

  return {
    debug: activeLevel <= LogLevel.DEBUG
      ? (...args: unknown[]) => console.debug(prefix, ...args)
      : () => {},

    info: activeLevel <= LogLevel.INFO
      ? (...args: unknown[]) => console.log(prefix, ...args)
      : () => {},

    warn: activeLevel <= LogLevel.WARN
      ? (...args: unknown[]) => console.warn(prefix, ...args)
      : () => {},

    error: activeLevel <= LogLevel.ERROR
      ? (...args: unknown[]) => console.error(prefix, ...args)
      : () => {},
  }
}
